/**
 * @fileoverview Cost of Sold Goods page with UTC-normalized month navigation, data table, and CSV export.
 * @module features/cogs/pages/CostOfSoldGoodsPage
 */

import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import PageMeta from '@/shared/components/common/PageMeta'
import { Button } from '@/shared/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronLeft, ChevronRight, Package, ArrowLeft } from 'lucide-react'
import { costOfSoldGoodsService } from '@/features/cogs/api/costOfSoldGoodsService'
import { getAllVariants } from '@/lib/variantService'
import type {
  CostOfSoldGoodsItemDTO,
  CostOfSoldGoodsResponseDTO,
} from '@/features/cogs/schemas/costOfSoldGoodsSchema'

/**
 * Convert a Date (interpreted in UTC) to MM-yyyy string for API.
 */
function formatMonthForApiUTC(dateUTC: Date): string {
  const monthNum = String(dateUTC.getUTCMonth() + 1).padStart(2, '0')
  const year = dateUTC.getUTCFullYear()
  return `${monthNum}-${year}`
}

/**
 * Human readable month label (e.g., "February 2025") from a UTC-based Date.
 */
function formatMonthDisplayUTC(dateUTC: Date): string {
  return dateUTC.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Get the first day of the previous month in UTC.
 */
function firstDayOfPreviousUtcMonth(base: Date): Date {
  const y = base.getUTCFullYear()
  const m = base.getUTCMonth()
  // Move to previous month; JS Date handles year wrap
  return new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0))
}

/**
 * Shift a UTC month by delta months, returning first day of target month in UTC.
 */
function shiftUtcMonth(dateUTC: Date, delta: number): Date {
  return new Date(Date.UTC(dateUTC.getUTCFullYear(), dateUTC.getUTCMonth() + delta, 1, 0, 0, 0, 0))
}

/**
 * Format a monetary amount with a currency symbol or code using Intl.NumberFormat.
 * Falls back to a fixed 2-decimal string if currency is not provided.
 *
 * @param amount The numeric amount to format.
 * @param currencyCode Optional ISO 4217 currency code (e.g., "USD", "EUR", "PLN").
 * @returns Formatted currency string.
 */
function formatCurrency(amount: number, currencyCode?: string): string {
  if (!Number.isFinite(amount)) return '0.00'
  if (!currencyCode) return amount.toFixed(2)
  try {
    const formatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    const parts = formatter.formatToParts(amount)
    const symbol = parts.find(p => p.type === 'currency')?.value ?? currencyCode
    const number = parts
      .filter(p => p.type !== 'currency' && p.type !== 'literal')
      .map(p => p.value)
      .join('')
    return `${number} ${symbol}`
  } catch {
    // Fallback for environments that do not support the provided currency code
    return `${amount.toFixed(2)} ${currencyCode}`
  }
}

/**
 * CostOfSoldGoodsPage component.
 *
 * @component
 * @returns ReactElement
 */
export default function CostOfSoldGoodsPage(): ReactElement {
  const { t } = useTranslation('features-cogs')
  const navigate = useNavigate()

  // Fetch all variants for product name and image lookup
  const { data: allVariants = [] } = useQuery({
    queryKey: ['all-variants'],
    queryFn: getAllVariants,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })

  // Create a lookup map by variant name for quick access
  const variantMap = useMemo(() => new Map(allVariants.map(v => [v.name, v])), [allVariants])

  // Selected month anchor (first day of month) in UTC
  const [selectedDateUTC, setSelectedDateUTC] = useState<Date>(() => {
    const now = new Date()
    return firstDayOfPreviousUtcMonth(now)
  })

  // Derived month (MM-yyyy) from UTC date
  const month = useMemo(() => formatMonthForApiUTC(selectedDateUTC), [selectedDateUTC])

  // Data state
  const [data, setData] = useState<CostOfSoldGoodsResponseDTO | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState<boolean>(false)

  const fetchData = useCallback(async () => {
    if (!month) return
    setLoading(true)
    setError(null)
    try {
      const response = await costOfSoldGoodsService.getCostOfSoldGoods({ month })
      setData(response)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred while fetching data'
      setError(message)
      console.error('Error fetching cost of sold goods:', err)
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  const handleExport = useCallback(async () => {
    if (!month) return
    setExporting(true)
    try {
      const blob = await costOfSoldGoodsService.exportCostOfSoldGoods({ month })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `cost-of-sold-goods-${month}.csv`
      document.body.appendChild(link)
      link.click()

      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred while exporting data'
      setError(message)
      console.error('Error exporting cost of sold goods:', err)
    } finally {
      setExporting(false)
    }
  }, [month])

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setSelectedDateUTC(prev => shiftUtcMonth(prev, direction === 'prev' ? -1 : 1))
  }, [])

  return (
    <>
      <PageMeta
        title={t('page.title', { defaultValue: 'Cost of Sold Goods' })}
        description={t('page.description', {
          defaultValue: 'View cost analysis of sold goods by month',
        })}
      />
      <div className="min-h-screen bg-card">
        <div className="mx-auto max-w-screen-2xl space-y-8 px-4 lg:px-8 pb-6 pt-10">
          {/* Header and Controls */}
          <div className="space-y-3">
            <div className="p-6 bg-card rounded-lg flex items-center gap-4">
              <Button onClick={() => navigate('/dashboard/orders')} variant="outline" size="lg">
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <div>
                <h1 className="text-3xl font-semibold text-foreground">
                  {t('page.title', { defaultValue: 'Cost of Sold Goods' })}
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('page.description', {
                    defaultValue: 'View cost analysis of sold goods by month',
                  })}
                </p>
              </div>
            </div>
            <div className="p-6 bg-card rounded-lg">
              <div className="flex items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {t('controls.selectMonth', { defaultValue: 'Select Month' })}
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-background border border-input rounded-lg h-10">
                    <button
                      aria-label={t('controls.prevMonth', { defaultValue: 'Previous month' })}
                      onClick={() => navigateMonth('prev')}
                      className="p-1 hover:bg-accent rounded transition-colors"
                      type="button"
                    >
                      <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                    </button>

                    <span className="min-w-[160px] text-center font-medium text-foreground">
                      {formatMonthDisplayUTC(selectedDateUTC)}
                    </span>

                    <button
                      aria-label={t('controls.nextMonth', { defaultValue: 'Next month' })}
                      onClick={() => navigateMonth('next')}
                      className="p-1 hover:bg-accent rounded transition-colors"
                      type="button"
                    >
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
                <Button
                  onClick={handleExport}
                  disabled={!data || exporting || loading}
                  variant="outline"
                  type="button"
                  size="default"
                  className="h-10"
                >
                  {exporting
                    ? t('controls.exporting', { defaultValue: 'Exporting...' })
                    : t('controls.exportCsv', { defaultValue: 'Export CSV' })}
                </Button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-xl border border-destructive/20">
              {error}
            </div>
          )}

          {/* Data Table */}
          <div className="px-6 pb-6 bg-card rounded-3xl border border-border/15 flex-1 flex flex-col">
            <div className="max-w-full overflow-x-auto flex-1">
              {loading ? (
                <div className="p-8 text-center">
                  {t('states.loading', { defaultValue: 'Loading cost of sold goods data...' })}
                </div>
              ) : !data ? (
                <div className="p-8 text-center">
                  {t('states.noData', {
                    defaultValue: 'No data available. Please select a month.',
                  })}
                </div>
              ) : data.items.length === 0 ? (
                <div className="p-8 text-center">
                  {t('states.noResultsForMonth', {
                    month,
                    defaultValue: `No sold goods found for ${month}`,
                  })}
                </div>
              ) : (
                <>
                  <Table className="table-fixed w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-6 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('table.sku', { defaultValue: 'SKU' })}
                        </TableHead>
                        <TableHead className="pl-6 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('table.unitsSold', { defaultValue: 'Units Sold' })}
                        </TableHead>
                        <TableHead className="pl-6 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('table.costPerSku', { defaultValue: 'Cost per SKU' })}
                        </TableHead>
                        <TableHead className="pl-6 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('table.totalCost', { defaultValue: 'Total Cost' })}
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {data.items.map((item: CostOfSoldGoodsItemDTO, index: number) => {
                        const variantData = variantMap.get(item.sku)
                        const productName = item.productName || variantData?.productName
                        const imageUrl = item.imageUrl || variantData?.imageUrl
                        return (
                          <TableRow key={`${item.sku}-${index}`}>
                            <TableCell className="pl-6">
                              <div className="flex items-start gap-3">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={productName || item.sku}
                                    className="size-12 rounded-md object-cover border border-border shrink-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer"
                                  />
                                ) : (
                                  <div className="size-12 rounded-md border border-dashed border-border/60 flex items-center justify-center text-muted-foreground shrink-0">
                                    <Package className="size-4" />
                                  </div>
                                )}
                                <div className="flex flex-col gap-1 min-w-0">
                                  <div className="text-sm text-foreground font-medium truncate">
                                    {productName || item.sku}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {item.sku}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="pl-6 text-sm text-muted-foreground">
                              {item.unitsSold}
                            </TableCell>
                            <TableCell className="pl-6 text-sm text-muted-foreground">
                              {formatCurrency(item.costPerSKU, data.currencyCode)}
                            </TableCell>
                            <TableCell className="pl-6 text-sm text-foreground font-semibold">
                              {formatCurrency(item.totalCost, data.currencyCode)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>

                  {/* Total Cost Summary */}
                  <div className="border-t border-border bg-muted/50 px-6 py-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-foreground">
                        {t('summary.totalCost', { defaultValue: 'Total Cost:' })}
                      </span>
                      <span className="text-lg font-bold text-foreground">
                        {formatCurrency(data.totalCost, data.currencyCode)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
